import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

import {
  toggleAsset,
  toggleNetwork,
  type CryptoOnchainAssetPick,
  type CryptoOnchainPickerRow,
} from './crypto-onchain-networks';

/** Where the catalog stands: being read, read, or not readable (the wallets still save without it). */
export type CryptoOnchainNetworksState = 'loading' | 'ready' | 'unavailable';

type RowProps = {
  row: CryptoOnchainPickerRow;
  picks: CryptoOnchainAssetPick;
  isPending: boolean;
  onChange: (picks: CryptoOnchainAssetPick) => void;
};

/** One network: its own checkbox (every coin at once), then one checkbox per coin. */
function NetworkRow({ row, picks, isPending, onChange }: RowProps) {
  const { t } = useTranslation('integrations');
  const { network, pickable } = row;
  const ticked = pickable ? (picks[String(network.chainId)] ?? []) : [];
  const isAllTicked = ticked.length === network.assets.length;
  const checked = isAllTicked ? true : ticked.length > 0 ? 'indeterminate' : false;
  const networkId = `crypto-onchain-network-${network.chainId}`;

  return (
    <fieldset className="space-y-2">
      <legend className="flex items-center gap-2">
        <Checkbox
          id={networkId}
          checked={checked}
          disabled={isPending || !pickable}
          onCheckedChange={(next) => onChange(toggleNetwork(picks, network, next === true))}
        />
        <Label htmlFor={networkId}>{network.name}</Label>
        {!pickable && (
          <Badge variant="secondary" className="text-xs">
            {t('cryptoOnchain.addresses.comingSoon')}
          </Badge>
        )}
      </legend>
      {pickable && (
        <div className="flex flex-wrap gap-x-4 gap-y-2 pl-6">
          {network.assets.map((asset) => {
            const assetId = `${networkId}-${asset.symbol}`;
            return (
              <div key={asset.symbol} className="flex items-center gap-2">
                <Checkbox
                  id={assetId}
                  checked={ticked.includes(asset.symbol)}
                  disabled={isPending}
                  onCheckedChange={(next) =>
                    onChange(toggleAsset(picks, network, asset.symbol, next === true))
                  }
                />
                <Label htmlFor={assetId} className="font-normal">
                  {asset.symbol}
                </Label>
              </div>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

type Props = {
  state: CryptoOnchainNetworksState;
  rows: CryptoOnchainPickerRow[];
  picks: CryptoOnchainAssetPick;
  error: string | null;
  isPending: boolean;
  onChange: (picks: CryptoOnchainAssetPick) => void;
};

/**
 * The networks and coins the checkout offers, in the store's mode (testnets in sandbox mode). A
 * network shows once its wallet is filled in; the kinds not offered yet show, disabled. The ticks
 * belong to the form, which sends them only when they changed.
 */
export function CryptoOnchainNetworksCard({ state, rows, picks, error, isPending, onChange }: Props) {
  const { t } = useTranslation('integrations');
  const hasNoWallet = state === 'ready' && rows.every((row) => !row.pickable);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cryptoOnchain.networks.title')}</CardTitle>
        <CardDescription>{t('cryptoOnchain.networks.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {state === 'loading' && (
          <p className="text-muted-foreground text-xs">{t('cryptoOnchain.networks.loading')}</p>
        )}
        {state === 'unavailable' && (
          <p className="text-muted-foreground text-xs">{t('cryptoOnchain.networks.unavailable')}</p>
        )}
        {hasNoWallet && (
          <p className="text-muted-foreground text-xs">{t('cryptoOnchain.networks.noWallet')}</p>
        )}
        {state === 'ready' &&
          rows.map((row) => (
            <NetworkRow
              key={row.network.chainId}
              row={row}
              picks={picks}
              isPending={isPending}
              onChange={onChange}
            />
          ))}
        {error && (
          <p role="alert" className="text-destructive text-xs">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
